import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Button,
  Box,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchDataFromAPI, saveData, deleteData } from "../components/api.js"; // Adjust import as necessary

const CreateFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(""); // 'add' or 'edit'
  const [currentFaq, setCurrentFaq] = useState({ question: "", response: "" });
  const [isOffline, setIsOffline] = useState(false); // Set this based on offline/online state

  // Function to fetch FAQs from the API
  const fetchData = async (page = 1, itemsPerPage = 10) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const queryParams = { page, limit: itemsPerPage };
      const queryString = new URLSearchParams(queryParams).toString();

      const { data: fetchedFaqs, pagination } = await fetchDataFromAPI(
        `/api/faqs`,
        queryString,
        token
      );

      setFaqs(fetchedFaqs || []);
      setTotalPages(pagination?.totalPages || 1);
    } catch (err) {
      setError("Error fetching FAQs");
      console.error("Error fetching FAQs:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, itemsPerPage); // Fetch FAQs on component mount and when page changes
  }, [page]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1); // Adjusting the page number as the pagination API uses 1-based index
  };

  // Handle Add FAQ dialog opening
  const handleAddFaq = () => {
    setDialogType("add");
    setCurrentFaq({ question: "", response: "" });
    setOpenDialog(true);
  };

  // Handle Edit FAQ dialog opening
  const handleEditFaq = (faq) => {
    setDialogType("edit");
    setCurrentFaq(faq);
    setOpenDialog(true);
  };

  // Handle Save FAQ (both add and edit)
  const handleSaveFaq = async () => {
    try {
      const newFaqData = {
        question: currentFaq.question,
        response: currentFaq.response,
      };

      if (isOffline) {
        storeOfflineData(
          [newFaqData],
          "faqs",
          dialogType === "edit" ? "update" : "add"
        ); // Store offline if in offline mode
      } else {
        const url =
          dialogType === "edit" ? `/api/faqs/${currentFaq.id}` : "/api/faqs";
        const method = dialogType === "edit" ? "PUT" : "POST";

        await saveData(url, newFaqData, method); // Save data using saveData method
      }
      fetchData(page, itemsPerPage); // Refresh FAQ list after saving
      setOpenDialog(false); // Close dialog after saving
    } catch (err) {
      setError("Error saving FAQ");
      console.error("Error saving FAQ:", err.message);
    }
  };

  // Handle Delete FAQ
  const handleDeleteFaq = async (id) => {
    try {
      if (isOffline) {
        storeOfflineData([{ id }], "faqs", "delete"); // Store the deletion offline
      } else {
        await deleteData(`/api/faqs/${id}`); // Delete FAQ via API
      }
      fetchData(page, itemsPerPage); // Refresh FAQ list after deletion
    } catch (err) {
      setError("Error deleting FAQ");
      console.error("Error deleting FAQ:", err.message);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#F4F6F9", // Dashboard background
        minHeight: "100vh",
        padding: "20px",
        position: "relative", // Make the container relative to position the button
      }}
    >
      <Typography
        variant="h4"
        style={{
          textAlign: "center",
          color: "#333333", // Dark gray text for the header
          marginBottom: "40px",
        }}
      >
        FAQs
      </Typography>

      {/* Add FAQ Button positioned to the upper right */}
      <Button
        onClick={handleAddFaq}
        variant="contained"
        color="primary"
        style={{
          position: "absolute", // Position button absolutely
          top: "20px", // Adjust the distance from the top
          right: "20px", // Adjust the distance from the right
          backgroundColor: "#2E4053", // Dark blue color for button
          color: "#FFFFFF",
        }}
      >
        Add FAQ
      </Button>

      {loading && <CircularProgress />}
      {error && <Typography color="error">{error}</Typography>}

      {/* Display FAQs in an Accordion format */}
      {!loading && !error && (
        <Box>
          {faqs.length === 0 ? (
            <Typography style={{ textAlign: "center", color: "#333333" }}>
              No FAQs available.
            </Typography>
          ) : (
            faqs.map((faq) => (
              <Accordion key={faq.id} style={{ marginBottom: "10px" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel-${faq.id}-content`}
                  id={`panel-${faq.id}-header`}
                  style={{
                    backgroundColor: "#FFFFFF", // White background for accordion header
                    border: "1px solid #D1D8E0", // Border for accordion
                  }}
                >
                  <Typography variant="h6" style={{ color: "#333333" }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ backgroundColor: "#FFFFFF" }}>
                  <Typography style={{ color: "#333333" }}>
                    {faq.response}
                  </Typography>

                  {/* Edit and Delete Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "10px",
                    }}
                  >
                    <Button
                      onClick={() => handleEditFaq(faq)}
                      variant="outlined"
                      color="primary"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteFaq(faq.id)}
                      variant="outlined"
                      color="secondary"
                    >
                      Delete
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "20px",
              }}
            >
              <Button
                onClick={(e) => handleChangePage(e, page - 2)}
                variant="outlined"
                color="primary"
                style={{
                  marginRight: "10px",
                  backgroundColor: "#A6AAAD", // Muted grayish-blue for button
                  color: "#2E4053", // Dark blue text
                }}
              >
                Previous
              </Button>
              <Typography style={{ margin: "0 10px", color: "#333333" }}>
                {`Page ${page} of ${totalPages}`}
              </Typography>
              <Button
                onClick={(e) => handleChangePage(e, page)}
                variant="outlined"
                color="primary"
                style={{
                  marginLeft: "10px",
                  backgroundColor: "#A6AAAD", // Muted grayish-blue for button
                  color: "#2E4053", // Dark blue text
                }}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Dialog for Adding/Editing FAQ */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {dialogType === "edit" ? "Edit FAQ" : "Add FAQ"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Question"
            variant="outlined"
            fullWidth
            value={currentFaq.question}
            onChange={(e) =>
              setCurrentFaq({ ...currentFaq, question: e.target.value })
            }
            style={{ marginBottom: "20px" }}
          />
          <TextField
            label="Answer"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={currentFaq.response}
            onChange={(e) =>
              setCurrentFaq({ ...currentFaq, response: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveFaq} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateFAQs;
